import {
  ComponentProps,
  ComponentType,
  useEffect,
  useRef,
  useState,
} from 'react';

const cache = new Map<string, ComponentType<ComponentProps<'svg'>>>();

export enum AssetsLoadingState {
  Initial,
  Loading,
  Loaded,
  Error,
}

export function useLazyLoadedAssets<TIconsList extends string>(
  name: TIconsList,
  assets: Record<
    `${string}.svg`,
    () => Promise<{ default: ComponentType<ComponentProps<'svg'>> }>
  >,
) {
  const iconName: TIconsList = name;

  // This ref is used to avoid re-rendering the component when the icon is loaded
  const SvgIconRef = useRef<ComponentType<ComponentProps<'svg'>> | undefined>(
    cache.get(iconName),
  );
  const [SvgIcon, setSvgIcon] = useState<
    ComponentType<ComponentProps<'svg'>> | undefined
  >(() => cache.get(iconName));
  // This state is used to track the loading state of the icon
  const [state, setState] = useState(
    cache.has(iconName)
      ? AssetsLoadingState.Loaded
      : AssetsLoadingState.Initial,
  );
  // This ref is used to track the icon name, so we can reload the icon if it changes
  const LoadedIcon = useRef<string>(iconName);
  useEffect(() => {
    let destroyed = false;
    if (!iconName) {
      setState(AssetsLoadingState.Error);
      return;
    }
    // If the icon is already loaded, we don't need to do anything
    if (SvgIconRef.current && LoadedIcon.current === iconName) {
      setState(AssetsLoadingState.Loaded);
      return;
      // If the icon is already in cache, we don't need to ask for it again
    } else if (cache.has(iconName)) {
      SvgIconRef.current = cache.get(iconName)!;
      LoadedIcon.current = iconName;
      setState(AssetsLoadingState.Loaded);
      setSvgIcon(() => SvgIconRef.current);
      return;
    }

    const path = `${iconName}.svg` as const;
    if (!assets[path]) {
      console.error(`Icon ${iconName} not found`);
      setState(AssetsLoadingState.Error);
      return;
    }
    setState(AssetsLoadingState.Loading);
    assets[path]()
      .then((icon) => {
        cache.set(iconName, icon.default);
        SvgIconRef.current = icon.default;
        LoadedIcon.current = iconName;
        setSvgIcon(() => SvgIconRef.current);

        // If the component was destroyed before the icon was loaded, we don't need to set the state
        if (destroyed) {
          return;
        }
        setState(AssetsLoadingState.Loaded);
      })
      .catch(() => {
        if (destroyed) {
          return;
        }
        setState(AssetsLoadingState.Error);
      });
    return () => {
      destroyed = true;
    };
  }, [iconName]);
  return { state, SvgIcon: SvgIcon };
}
